/** @file Simple switch component */

import React from 'react';
import { Box, BoxProps, SxProps } from '@mui/material';

/** Styling */
const makeStyles = ({
  isChecked,
  isDisabled,
}: {
  isChecked: boolean;
  isDisabled?: boolean;
}): { [id: string]: SxProps } => ({
  root: {
    alignItems: 'center',
    backgroundColor: !isChecked ? 'white' : 'primary.dark',
    border: '1px solid',
    borderColor: isDisabled ? 'text.secondary' : 'primary.dark',
    borderRadius: '85px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    height: '24px',
    width: '44px',
    minWidth: '44px',
    transition: 'all 500ms',
    opacity: isDisabled ? '50%' : undefined,
  },
  font: {
    fontSize: '9px',
    color: !isChecked ? 'primary.dark' : 'white',
    transition: 'all 500ms',
    opacity: isDisabled ? '50%' : undefined,
  },
  toggle: {
    backgroundColor: !isChecked ? 'primary.dark' : 'white',
    borderRadius: '18px',
    height: 18,
    minWidth: 18,
    transform: `translateX(${!isChecked ? '-95%' : '20%'})`,
    transition: 'all 500ms',
  },
});

interface Props extends BoxProps {
  isChecked: boolean;
  onToggle: (newValue: boolean) => unknown;
  isDisabled?: boolean;
}

/**
 * Basic switch component
 * @returns {JSX.Element}
 */
const Switch = ({ isChecked, onToggle, isDisabled, ...boxProps }: Props) => {
  const styles = makeStyles({ isChecked, isDisabled });

  return (
    <Box
      className={`gc-switch root ${isChecked ? 'is-checked' : 'is-not-checked'} ${
        isDisabled ? 'disabled' : 'enabled'
      }`}
      sx={styles.root}
      onClick={isDisabled ? () => {} : () => onToggle(!isChecked)}
      role="switch"
      aria-checked={isChecked}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...boxProps}
    >
      <Box
        className="gc-switch checked-copy"
        sx={{
          ...styles.font,
          marginLeft: '6px',
          marginTop: '1px',
          opacity: isChecked ? 100 : 0,
        }}
      >
        ON
      </Box>
      <Box className="gc-switch toggle" sx={styles.toggle} />
      <Box
        className="gc-switch unchecked-copy"
        sx={{
          ...styles.font,
          marginLeft: '-15px',
          marginTop: '1px',
          opacity: isChecked ? 0 : 100,
        }}
      >
        OFF
      </Box>
    </Box>
  );
};

export default Switch;
